import { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@components/Button';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import { MultiSelect } from '@components/MultiSelect';
import { useTranslation } from '@hooks';
import { CONDITION_OPERATOR, EVENT_FIELD_INPUT } from '@constants/automations';
import {
    ROW_KIND,
    CONTACT_FIELDS,
    EVENT_FIELD_OPERATORS,
    NO_VALUE_OPERATORS,
    findContactField,
    findEventField,
    isRowIncomplete,
    newSegmentRow,
    newFieldRow,
    newEventRow,
    newScenario,
    normalizeScenarios,
    scenariosToGroup,
    splitIds,
} from './conditions';

const VALUE_OPERATORS = [
    { value: CONDITION_OPERATOR.EQUALS, key: 'is' },
    { value: CONDITION_OPERATOR.NOT_EQUALS, key: 'isNot' },
    { value: CONDITION_OPERATOR.IN, key: 'isOneOf' },
    { value: CONDITION_OPERATOR.CONTAINS, key: 'contains' },
    { value: CONDITION_OPERATOR.NOT_CONTAINS, key: 'notContains' },
    { value: CONDITION_OPERATOR.EXISTS, key: 'isKnown' },
    { value: CONDITION_OPERATOR.NOT_EXISTS, key: 'isUnknown' },
    { value: CONDITION_OPERATOR.GREATER_THAN, key: 'greaterThan' },
    { value: CONDITION_OPERATOR.LESS_THAN, key: 'lessThan' },
];

const NUMBER_INPUT_PROPS = {
    [EVENT_FIELD_INPUT.INTEGER]: { inputMode: 'numeric', step: 1 },
    [EVENT_FIELD_INPUT.DECIMAL]: { inputMode: 'decimal' },
};

const EVENT_FIELD_PREFIX = 'event:';

const whatValueOf = (row, eventField) => {
    if (row.kind === ROW_KIND.SEGMENT) return 'segment';
    if (row.kind === ROW_KIND.EVENT) return eventField ? `${EVENT_FIELD_PREFIX}${eventField.path}` : 'event';
    return `field:${row.fieldKey}`;
};

const rowForWhat = (what, eventFields) => {
    if (what === 'segment') return newSegmentRow();
    if (what === 'event') return newEventRow();
    if (what.startsWith(EVENT_FIELD_PREFIX)) {
        const field = eventFields.find(item => item.path === what.slice(EVENT_FIELD_PREFIX.length));
        return {
            ...newEventRow(),
            path: field.path,
            operator: EVENT_FIELD_OPERATORS[field.input][0].value,
            value: field.input === EVENT_FIELD_INPUT.BOOLEAN ? 'true' : '',
        };
    }
    return newFieldRow(what.slice('field:'.length));
};

const ConditionRow = ({
    row,
    labels,
    allowEventField,
    eventFields,
    eventFieldOptions,
    onUpdate,
    onReplace,
    onRemove,
}) => {
    const { t } = useTranslation();

    const incomplete = isRowIncomplete(row);
    const isKnownField = row.kind !== ROW_KIND.FIELD || CONTACT_FIELDS.some(field => field.value === row.fieldKey);
    const eventField = findEventField(row, eventFields);
    const typedField = eventField || findContactField(row);

    const whatOptions = [
        { value: 'segment', label: t('AutomationsPage.conditions.segment') },
        ...CONTACT_FIELDS.map(field => ({ value: `field:${field.value}`, label: t(field.labelKey) })),
        ...(isKnownField ? [] : [{ value: `field:${row.fieldKey}`, label: row.fieldKey }]),
        ...eventFields.map(field => ({
            value: `${EVENT_FIELD_PREFIX}${field.path}`,
            label: t(`AutomationsPage.conditions.eventFields.${field.key}`),
        })),
        ...(allowEventField || (row.kind === ROW_KIND.EVENT && !eventField)
            ? [{ value: 'event', label: t('AutomationsPage.conditions.eventField') }]
            : []),
    ];

    const operatorOptions =
        row.kind === ROW_KIND.SEGMENT
            ? [
                  { value: 0, label: t('AutomationsPage.conditions.operators.isAnyOf') },
                  { value: 1, label: t('AutomationsPage.conditions.operators.isNoneOf') },
              ]
            : (typedField ? typedField.operators || EVENT_FIELD_OPERATORS[typedField.input] : VALUE_OPERATORS).map(
                  op => ({
                      value: op.value,
                      label: t(`AutomationsPage.conditions.operators.${op.key}`),
                  }),
              );

    const valueControl = NO_VALUE_OPERATORS.includes(row.operator) ? null : typedField?.input;
    const showValueInput =
        row.kind !== ROW_KIND.SEGMENT &&
        !NO_VALUE_OPERATORS.includes(row.operator) &&
        valueControl !== EVENT_FIELD_INPUT.SELECT &&
        valueControl !== EVENT_FIELD_INPUT.BOOLEAN;

    const selectedValues = splitIds(row.value);
    const availableOptions =
        valueControl === EVENT_FIELD_INPUT.SELECT ? eventFieldOptions[typedField.options] || [] : [];
    const pickerOptions = [
        ...availableOptions,
        ...(valueControl === EVENT_FIELD_INPUT.SELECT ? selectedValues : [])
            .filter(value => !availableOptions.some(o => o.value === value))
            .map(value => ({ value, label: t('AutomationsPage.conditions.unavailableOption') })),
    ];

    return (
        <Box
            sx={{
                border: `1px solid ${incomplete ? 'var(--error-main)' : 'var(--divider)'}`,
                borderRadius: '8px',
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}
        >
            {/* The shared field root carries 16px ghost bottom padding, which pushes a centred
                icon below the visible input. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&& .MuiFormControl-root': { paddingBottom: 0 },
                }}
            >
                <Select
                    style={{ flex: 1, minWidth: 0 }}
                    value={whatValueOf(row, eventField)}
                    onChange={e => onReplace({ ...rowForWhat(e.target.value, eventFields), key: row.key })}
                    options={whatOptions}
                    fullWidth
                />
                <IconButton size="small" sx={{ flexShrink: 0 }} onClick={onRemove}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Select
                value={row.kind === ROW_KIND.SEGMENT ? (row.negate ? 1 : 0) : row.operator}
                onChange={e =>
                    row.kind === ROW_KIND.SEGMENT
                        ? onUpdate({ negate: Number(e.target.value) === 1 })
                        : onUpdate({ operator: Number(e.target.value) })
                }
                options={operatorOptions}
                fullWidth
            />

            {row.kind === ROW_KIND.SEGMENT && (
                <MultiSelectLabels
                    label={t('AutomationsPage.conditions.segments')}
                    value={row.ids}
                    onChange={ids => onUpdate({ ids })}
                    options={[
                        ...labels.map(label => ({ value: label.id, label: label.name })),
                        ...row.ids
                            .filter(id => !labels.some(label => label.id === id))
                            .map(id => ({ value: id, label: t('AutomationsPage.conditions.deletedSegment') })),
                    ]}
                    editable={false}
                />
            )}

            {valueControl === EVENT_FIELD_INPUT.SELECT && (
                <MultiSelect
                    label={t('AutomationsPage.conditions.value')}
                    value={selectedValues}
                    options={pickerOptions}
                    onChange={values => onUpdate({ value: values.join(',') })}
                    allOption={false}
                    fullWidth
                />
            )}

            {valueControl === EVENT_FIELD_INPUT.BOOLEAN && (
                <Select
                    value={row.value}
                    onChange={e => onUpdate({ value: e.target.value })}
                    options={[
                        { value: 'true', label: t('common.yes') },
                        { value: 'false', label: t('common.no') },
                    ]}
                    fullWidth
                />
            )}

            {row.kind === ROW_KIND.EVENT && !eventField && (
                <Input
                    label={t('AutomationsPage.conditions.eventField')}
                    placeholder={t('AutomationsPage.conditions.jsonPathPlaceholder')}
                    inputProps={{
                        value: row.path,
                        onChange: e => onUpdate({ path: e.target.value }),
                    }}
                    fullWidth
                />
            )}

            {showValueInput && (
                <Input
                    type={NUMBER_INPUT_PROPS[valueControl] ? 'number' : 'text'}
                    label={t('AutomationsPage.conditions.value')}
                    placeholder={t(
                        row.operator === CONDITION_OPERATOR.IN
                            ? 'AutomationsPage.conditions.listPlaceholder'
                            : 'AutomationsPage.conditions.valuePlaceholder',
                    )}
                    inputProps={{
                        value: row.value,
                        onChange: e => onUpdate({ value: e.target.value }),
                        ...NUMBER_INPUT_PROPS[valueControl],
                        ...(typedField?.max ? { min: 0, max: typedField.max } : {}),
                    }}
                    fullWidth
                />
            )}

            {incomplete && (
                <Typography variant="caption" sx={{ color: 'var(--error-main)' }}>
                    {t('AutomationsPage.conditions.incompleteFilter')}
                </Typography>
            )}
        </Box>
    );
};

const Connector = ({ children }) => (
    <Typography variant="caption" color="textSecondary" sx={{ pl: 1, textTransform: 'uppercase' }}>
        {children}
    </Typography>
);

const ConditionBuilder = ({
    value,
    onChange,
    labels = [],
    allowEventField = false,
    eventFields = [],
    eventFieldOptions = {},
}) => {
    const { t } = useTranslation();
    const [scenarios, setScenarios] = useState(() => normalizeScenarios(value));

    const emit = next => {
        setScenarios(next);
        onChange(scenariosToGroup(next));
    };

    if (scenarios === null) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                <Typography variant="caption" color="textSecondary">
                    {t('AutomationsPage.conditions.tooComplex')}
                </Typography>
                <Button variant="text" size="small" onClick={() => emit([])}>
                    {t('AutomationsPage.conditions.clearFilters')}
                </Button>
            </Box>
        );
    }

    const updateScenario = (index, rows) => {
        const next = scenarios
            .map((scenario, i) => (i === index ? { ...scenario, rows } : scenario))
            .filter(scenario => scenario.rows.length);
        emit(next);
    };

    const addRow = index => updateScenario(index, [...scenarios[index].rows, newSegmentRow()]);
    const addScenario = () => emit([...scenarios, newScenario()]);
    const removeScenario = index => emit(scenarios.filter((_, i) => i !== index));

    if (!scenarios.length) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                <Typography variant="caption" color="textSecondary">
                    {t('AutomationsPage.conditions.everyoneEnters')}
                </Typography>
                <Button variant="text" size="small" onClick={addScenario}>
                    + {t('AutomationsPage.conditions.addFilter')}
                </Button>
            </Box>
        );
    }

    const multi = scenarios.length > 1;

    const renderScenario = (scenario, index) => (
        <Box
            key={scenario.key}
            sx={{
                border: multi ? '1px solid var(--divider)' : 'none',
                borderRadius: multi ? '8px' : 0,
                p: multi ? 1.5 : 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {multi && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="textSecondary">
                        {t('AutomationsPage.conditions.scenario', { number: index + 1 })}
                    </Typography>
                    <IconButton size="small" onClick={() => removeScenario(index)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {scenario.rows.map((row, rowIndex) => (
                <Box key={row.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rowIndex > 0 && <Connector>{t('AutomationsPage.conditions.and')}</Connector>}
                    <ConditionRow
                        row={row}
                        labels={labels}
                        allowEventField={allowEventField}
                        eventFields={eventFields}
                        eventFieldOptions={eventFieldOptions}
                        onUpdate={patch =>
                            updateScenario(
                                index,
                                scenario.rows.map((r, i) => (i === rowIndex ? { ...r, ...patch } : r)),
                            )
                        }
                        onReplace={next =>
                            updateScenario(
                                index,
                                scenario.rows.map((r, i) => (i === rowIndex ? next : r)),
                            )
                        }
                        onRemove={() =>
                            updateScenario(
                                index,
                                scenario.rows.filter((_, i) => i !== rowIndex),
                            )
                        }
                    />
                </Box>
            ))}

            <Box>
                <Button variant="text" size="small" onClick={() => addRow(index)}>
                    + {t('AutomationsPage.conditions.addFilter')}
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="textSecondary">
                {t(multi ? 'AutomationsPage.conditions.matchAnyHint' : 'AutomationsPage.conditions.matchAllHint')}
            </Typography>

            {scenarios.map((scenario, index) => (
                <Box key={scenario.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {index > 0 && <Connector>{t('AutomationsPage.conditions.or')}</Connector>}
                    {renderScenario(scenario, index)}
                </Box>
            ))}

            <Box>
                <Button variant="text" size="small" onClick={addScenario}>
                    + {t('AutomationsPage.conditions.addAlternative')}
                </Button>
            </Box>
        </Box>
    );
};

export default ConditionBuilder;
